import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, AlertCircle, TrendingDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "@/lib/i18n";
import InfoTooltip from "@/components/portfolio/InfoTooltip";
import { fmtCurrency, fmtPercent, fmtNumber } from "@/components/portfolio/_format";

/**
 * Locale-tolerant decimal parser.
 *
 * Accepts both "1,234.56" (en) and "1.234,56" (de) strings as well as
 * numeric input. Returns `null` if the input is blank or unparseable.
 *
 * The frontend NEVER ships these values to the backend in their parsed
 * form — the raw string goes through and the backend has its own
 * Decimal-safe parser. This helper exists only to drive the live
 * preview card.
 */
function parseLocaleDecimal(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  let s = String(raw).trim();
  if (!s) return null;
  if (s.indexOf(",") >= 0 && s.indexOf(".") >= 0) {
    s = s.lastIndexOf(",") > s.lastIndexOf(".")
      ? s.replace(/\./g, "").replace(",", ".")
      : s.replace(/,/g, "");
  } else if (s.indexOf(",") >= 0) {
    s = s.replace(",", ".");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Generate a uuid-ish idempotency key. We don't need cryptographic
 * uniqueness — a single user firing the modal twice in 500ms is the
 * worst case the backend cares about. */
function generateIdempotencyKey() {
  return "fe-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const todayIsoDate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * RecordSaleModal — Phase 8.C-2.
 *
 * Records a SELL transaction against the position's active lifecycle.
 * Live preview is informational only — the backend response is the
 * authoritative result. We deliberately do NOT mutate any portfolio
 * state from inside this component; the parent does so after a
 * successful POST.
 *
 * Backend contract (Phase 8.C-1):
 *   POST /api/portfolio/holdings/{ticker}/sell
 *     body: { shares, price_per_share, transaction_date?, fees?, notes?, idempotency_key }
 *     200 → { transaction, remaining_shares, avg_buy_price, closed_position? }
 *     409 → detail.error_code in {insufficient_shares, conflict}
 *     400 → detail.error_code in {invalid_*}
 */
export default function RecordSaleModal({
  open,
  onOpenChange,
  position,           // open-positions row from /api/portfolio
  onSubmit,           // async (payload) => result; parent posts to API
  busy = false,       // controlled by parent (kept here for clarity)
  errorMessage = null,
}) {
  const { t } = useTranslation();
  const idemKeyRef = useRef(generateIdempotencyKey());

  const [shares, setShares] = useState("");
  const [price, setPrice] = useState("");
  const [date, setDate] = useState(todayIsoDate());
  const [fees, setFees] = useState("0");
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the modal opens for a (potentially) new ticker.
  useEffect(() => {
    if (open) {
      idemKeyRef.current = generateIdempotencyKey();
      setShares("");
      setPrice("");
      setDate(todayIsoDate());
      setFees("0");
      setNotes("");
      setTouched({});
      setSubmitting(false);
    }
  }, [open, position?.ticker]);

  const availableShares = Number(position?.shares || 0);
  const avgBuy = Number(position?.buy_price || 0);
  const currentPrice = Number(position?.current_price || 0);
  const currentUnrealized = position?.unrealized_pnl;
  const openCostBasis = availableShares * avgBuy;
  const cur = position?.currency || "USD";

  // ---- Live preview (informational) ---------------------------------
  const sharesN = parseLocaleDecimal(shares);
  const priceN = parseLocaleDecimal(price);
  const feesN = parseLocaleDecimal(fees) ?? 0;
  const preview = useMemo(() => {
    if (sharesN === null || priceN === null || sharesN <= 0 || priceN <= 0) {
      return null;
    }
    const gross = sharesN * priceN;
    const net = gross - Math.max(feesN, 0);
    const costBasisReleased = sharesN * avgBuy;
    const realized = net - costBasisReleased;
    const realizedPct = costBasisReleased > 0 ? realized / costBasisReleased : 0;
    // Match the backend's dust-tolerance: anything within 1 micro-share
    // of available is treated as a full close.
    const wouldFullClose = Math.abs(sharesN - availableShares) <= 1e-6 || sharesN >= availableShares;
    const remainingShares = Math.max(0, availableShares - sharesN);
    const remainingCostBasis = wouldFullClose ? 0 : remainingShares * avgBuy;
    return {
      gross,
      fees: Math.max(feesN, 0),
      net,
      costBasisReleased,
      realized,
      realizedPct,
      remainingShares: wouldFullClose ? 0 : remainingShares,
      remainingCostBasis,
      wouldFullClose,
    };
  }, [sharesN, priceN, feesN, avgBuy, availableShares]);

  // ---- Validation ---------------------------------------------------
  const errors = useMemo(() => {
    const e = {};
    if (touched.shares) {
      if (sharesN === null || sharesN <= 0) e.shares = t("portfolio.sell.validation_shares_positive");
      else if (sharesN > availableShares + 1e-6) e.shares = t("portfolio.sell.validation_shares_max");
    }
    if (touched.price && (priceN === null || priceN <= 0)) e.price = t("portfolio.sell.validation_price_positive");
    if (touched.fees && feesN < 0) e.fees = t("portfolio.sell.validation_fees_nonneg");
    if (touched.date && date) {
      // Accept yyyy-mm-dd
      const ok = /^\d{4}-\d{2}-\d{2}$/.test(date) && !Number.isNaN(new Date(date).getTime());
      if (!ok) e.date = t("portfolio.sell.validation_date_invalid");
    }
    return e;
  }, [touched, sharesN, priceN, feesN, date, availableShares, t]);

  const canSubmit = !!preview && Object.keys(errors).length === 0 && !submitting && !busy;

  const handleSellAll = () => {
    // Use the EXACT backend share value (string-cast, no trimming) so
    // float→Decimal coercion can't introduce dust on the server side.
    // 6dp matches SHARES_DP on the backend.
    const exact = Number(availableShares).toFixed(6).replace(/\.?0+$/, "");
    setShares(exact);
    setTouched((p) => ({ ...p, shares: true }));
  };

  const handleSubmit = async () => {
    setTouched({ shares: true, price: true, fees: true, date: true });
    if (!preview || Object.keys(errors).length > 0) return;
    setSubmitting(true);
    try {
      await onSubmit?.({
        ticker: position?.ticker,
        shares: shares.trim(),
        price_per_share: price.trim(),
        transaction_date: date || null,
        fees: fees.trim() || 0,
        notes: notes.trim() || null,
        idempotency_key: idemKeyRef.current,
        // Echo the preview shape so the parent can pick the right toast.
        wouldFullClose: preview.wouldFullClose,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const headerSubtitle = position?.company_name || position?.ticker || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="record-sale-modal"
        className="max-w-[680px] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-[#FF3DA6]" />
            <span data-testid="record-sale-modal-title">{t("portfolio.sell.title")}</span>
            {position?.ticker ? (
              <span className="ml-2 text-base font-mono opacity-80">{position.ticker}</span>
            ) : null}
          </DialogTitle>
          <div className="text-sm text-muted-foreground" data-testid="record-sale-modal-subtitle">
            {headerSubtitle}
          </div>
        </DialogHeader>

        {/* Position summary header */}
        <div
          data-testid="record-sale-position-summary"
          className="grid grid-cols-2 sm:grid-cols-3 gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs"
        >
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("portfolio.sell.available_shares")}
            </div>
            <div className="tabular text-sm" data-testid="record-sale-available-shares">
              {fmtNumber(availableShares, { digits: 6 })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("portfolio.sell.avg_buy_price")}
            </div>
            <div className="tabular text-sm" data-testid="record-sale-avg-buy">
              {fmtCurrency(avgBuy, { currency: cur })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("portfolio.sell.open_cost_basis")}
            </div>
            <div className="tabular text-sm" data-testid="record-sale-open-cost-basis">
              {fmtCurrency(openCostBasis, { currency: cur })}
            </div>
          </div>
          {currentPrice > 0 ? (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("portfolio.sell.current_price")}
              </div>
              <div className="tabular text-sm">{fmtCurrency(currentPrice, { currency: cur })}</div>
            </div>
          ) : null}
          {currentUnrealized !== null && currentUnrealized !== undefined ? (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("portfolio.sell.current_unrealized")}
              </div>
              <div className={`tabular text-sm ${Number(currentUnrealized) >= 0 ? "text-[#2BFF8A]" : "text-[#FF3DA6]"}`}>
                {fmtCurrency(currentUnrealized, { currency: cur, signed: true })}
              </div>
            </div>
          ) : null}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("portfolio.sell.open_status")}
            </div>
            <Badge variant="outline" className="border-[#2BFF8A]/40 text-[#2BFF8A] bg-[#2BFF8A]/10 text-[10px]">
              {t("portfolio.sell.status_open")}
            </Badge>
          </div>
        </div>

        {/* Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="grid gap-1">
            <Label htmlFor="record-sale-shares">{t("portfolio.sell.field_shares")}</Label>
            <div className="flex gap-2">
              <Input
                id="record-sale-shares"
                inputMode="decimal"
                value={shares}
                onChange={(e) => { setShares(e.target.value); }}
                onBlur={() => setTouched((p) => ({ ...p, shares: true }))}
                placeholder="0"
                data-testid="record-sale-input-shares"
                aria-invalid={!!errors.shares}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={handleSellAll}
                data-testid="record-sale-sell-all"
              >
                {t("portfolio.sell.sell_all")}
              </Button>
            </div>
            {errors.shares ? (
              <div className="text-[11px] text-[#FF3DA6]" data-testid="record-sale-error-shares">{errors.shares}</div>
            ) : null}
          </div>
          <div className="grid gap-1">
            <Label htmlFor="record-sale-price">{t("portfolio.sell.field_price")}</Label>
            <Input
              id="record-sale-price"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              onBlur={() => setTouched((p) => ({ ...p, price: true }))}
              placeholder="0.00"
              data-testid="record-sale-input-price"
              aria-invalid={!!errors.price}
            />
            {errors.price ? (
              <div className="text-[11px] text-[#FF3DA6]" data-testid="record-sale-error-price">{errors.price}</div>
            ) : null}
          </div>
          <div className="grid gap-1">
            <Label htmlFor="record-sale-date" className="flex items-center gap-1.5">
              <span>{t("portfolio.sell.field_date")}</span>
              <InfoTooltip
                text={t("portfolio.sell.field_date_tooltip")}
                label={t("portfolio.sell.field_date_tooltip_label")}
                testId="record-sale-date-tooltip"
                side="top"
              />
            </Label>
            <Input
              id="record-sale-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              onBlur={() => setTouched((p) => ({ ...p, date: true }))}
              data-testid="record-sale-input-date"
              aria-invalid={!!errors.date}
            />
            {errors.date ? <div className="text-[11px] text-[#FF3DA6]">{errors.date}</div> : null}
          </div>
          <div className="grid gap-1">
            <Label htmlFor="record-sale-fees">{t("portfolio.sell.field_fees")}</Label>
            <Input
              id="record-sale-fees"
              inputMode="decimal"
              value={fees}
              onChange={(e) => setFees(e.target.value)}
              onBlur={() => setTouched((p) => ({ ...p, fees: true }))}
              placeholder="0.00"
              data-testid="record-sale-input-fees"
              aria-invalid={!!errors.fees}
            />
            {errors.fees ? <div className="text-[11px] text-[#FF3DA6]">{errors.fees}</div> : null}
          </div>
          <div className="grid gap-1 sm:col-span-2">
            <Label htmlFor="record-sale-notes">{t("portfolio.sell.field_notes")}</Label>
            <Textarea
              id="record-sale-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={400}
              data-testid="record-sale-input-notes"
            />
          </div>
        </div>

        {/* Preview */}
        <Separator className="my-1" />
        <div data-testid="record-sale-preview" className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-sm font-medium">{t("portfolio.sell.preview_title")}</div>
            <div className="text-[10px] text-muted-foreground">{t("portfolio.sell.preview_caveat")}</div>
          </div>
          {!preview ? (
            <div className="text-xs text-muted-foreground" data-testid="record-sale-preview-empty">
              {/* Empty state until user fills shares + price. */}
              —
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              <PreviewRow label={t("portfolio.sell.preview_gross")} value={fmtCurrency(preview.gross, { currency: cur })} testId="preview-gross" />
              <PreviewRow label={t("portfolio.sell.preview_fees")} value={fmtCurrency(preview.fees, { currency: cur })} testId="preview-fees" />
              <PreviewRow label={t("portfolio.sell.preview_net")} value={fmtCurrency(preview.net, { currency: cur })} testId="preview-net" />
              <PreviewRow label={t("portfolio.sell.preview_cost_basis_released")} value={fmtCurrency(preview.costBasisReleased, { currency: cur })} testId="preview-cbr" />
              <PreviewRow
                label={t("portfolio.sell.preview_realized_pl")}
                value={fmtCurrency(preview.realized, { currency: cur, signed: true })}
                tone={preview.realized >= 0 ? "good" : "bad"}
                testId="preview-realized-pl"
              />
              <PreviewRow
                label={t("portfolio.sell.preview_realized_pl_pct")}
                value={fmtPercent(preview.realizedPct)}
                tone={preview.realized >= 0 ? "good" : "bad"}
                testId="preview-realized-pl-pct"
              />
              <PreviewRow label={t("portfolio.sell.preview_remaining_shares")} value={fmtNumber(preview.remainingShares, { digits: 6 })} testId="preview-remaining-shares" />
              <PreviewRow label={t("portfolio.sell.preview_remaining_cost_basis")} value={fmtCurrency(preview.remainingCostBasis, { currency: cur })} testId="preview-remaining-cb" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t("portfolio.sell.preview_status")}
                </div>
                <Badge
                  variant="outline"
                  className={preview.wouldFullClose
                    ? "border-[#FF3DA6]/40 text-[#FF3DA6] bg-[#FF3DA6]/10 text-[10px]"
                    : "border-[#2BFF8A]/40 text-[#2BFF8A] bg-[#2BFF8A]/10 text-[10px]"}
                  data-testid="preview-status-badge"
                >
                  {preview.wouldFullClose ? t("portfolio.sell.status_closed") : t("portfolio.sell.status_open")}
                </Badge>
              </div>
            </div>
          )}
        </div>

        {errorMessage ? (
          <div
            data-testid="record-sale-server-error"
            className="rounded-lg border border-[#FF3DA6]/40 bg-[#FF3DA6]/10 px-3 py-2 text-xs text-[#FF7AC3] flex items-start gap-2"
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-[1px]" />
            <span>{errorMessage}</span>
          </div>
        ) : null}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting || busy}
            data-testid="record-sale-cancel-button"
          >
            {t("portfolio.sell.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="record-sale-submit-button"
            className="bg-[#FF3DA6] hover:bg-[#FF3DA6]/90 text-white"
          >
            {(submitting || busy) ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("portfolio.sell.submitting")}
              </>
            ) : (
              t("portfolio.sell.submit")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewRow({ label, value, tone, testId }) {
  const toneCls = tone === "good"
    ? "text-[#2BFF8A]"
    : tone === "bad"
      ? "text-[#FF3DA6]"
      : "";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`tabular text-sm ${toneCls}`} data-testid={testId}>{value}</div>
    </div>
  );
}
